import './qbooksShippingSection.scss';

import { DateTimeEditor, DropdownEditor, Field, TextAreaEditor, TextEditor } from '@approvalmax/ui';
import { constants } from 'modules/common';
import { domain, State } from 'modules/data';
import { PlainDataProvider } from 'modules/data-providers';
import { ConnectedProps } from 'modules/react-redux';
import React from 'react';
import bemFactory from 'react-bem-factory';
import { connect } from 'react-redux';

import {
    changeQbooksCustomField,
    changeQbooksDate,
    changeQbooksDepartment,
    changeQbooksShippingAddress,
    changeQbooksShippingCustomer,
    changeQbooksShippingMethod,
} from '../../../../../actions';
import { SectionContainer } from '../../../../../components/SectionContainer';
import { getDepartmentsWithNesting, QBooksDepartment } from '../../../../../data/qbooks/QBooksDepartment';
import { getContext, getRequest, getShowValidationErrors } from '../../../../../selectors/pageSelectors';
import { getIsRequiredLocation, getQBooksContext } from '../../../../../selectors/qbooks';
import { CustomerDataProvider } from '../../../dataProviders/CustomerDataProvider/CustomerDataProvider';
import { messages } from './QBooksShippingSection.messages';

function mapStateToProps(state: State) {
    const request = getRequest<domain.QBooksPoRequest>(state);
    const details = request.details;

    return {
        context: getContext(state),
        qbooksContext: getQBooksContext(state),
        date: details.date,
        department: details.department,
        shipTo: details.shipping ? details.shipping.customer : undefined,
        shipVia: details.shipping ? details.shipping.method : undefined,
        shippingAddress: details.shipping ? details.shipping.address : undefined,
        customFields: details.customFields,
        showValidationErrors: getShowValidationErrors(state),
        isRequiredLocation: getIsRequiredLocation(state, request),
    };
}

const mapDispatchToProps = {
    changeQbooksDate,
    changeQbooksDepartment,
    changeQbooksShippingAddress,
    changeQbooksShippingCustomer,
    changeQbooksShippingMethod,
    changeQbooksCustomField,
};

interface OwnProps {
    className?: string;
}

interface OwnState {}

type Props = ConnectedProps<OwnProps, typeof mapStateToProps, typeof mapDispatchToProps>;

class QBooksShippingSection extends React.PureComponent<Props, OwnState> {
    public render() {
        const {
            context,
            className,
            qbooksContext,
            date,
            department,
            shipTo,
            shipVia,
            shippingAddress,
            customFields,
            showValidationErrors,
            isRequiredLocation,
        } = this.props;
        const bem = bemFactory.block('reqf-qbooks-shipping-section');
        const qa = bemFactory.qa('reqf-qbooks-shipping-section');

        const allCustomFields = context.fields.filter(
            (f) => f.systemPurpose === domain.FieldSystemPurpose.QBooksCustom
        );

        const departments = getDepartmentsWithNesting(qbooksContext.departments || []);

        return (
            <SectionContainer className={bem.add(className)()} qa={qa()} title={messages.sectionName}>
                <div className={bem('content')}>
                    <div className={bem('primary-block')}>
                        <div className={bem('primary-col-1')}>
                            <Field required title={messages.dateField}>
                                <DateTimeEditor
                                    qa={qa('date-editor')}
                                    invalid={showValidationErrors && !date}
                                    value={date}
                                    onChange={this.props.changeQbooksDate}
                                    placeholder={messages.datePlaceholder}
                                />
                            </Field>

                            {qbooksContext.hasDepartmentFeature && (
                                <Field
                                    title={qbooksContext.departmentField!.text}
                                    className={bem('primary-row-2')}
                                    required={isRequiredLocation}
                                >
                                    <PlainDataProvider items={departments}>
                                        <DropdownEditor
                                            invalid={showValidationErrors && isRequiredLocation && !department}
                                            qa={qa('department-editor')}
                                            value={department}
                                            onChange={this.props.changeQbooksDepartment}
                                            placeholder={messages.departmentPlaceholder({
                                                fieldText: qbooksContext.departmentField!.text,
                                            })}
                                            listItem={(props) => (
                                                <DropdownEditor.ListItem
                                                    {...props}
                                                    nestingLevel={(props.item as QBooksDepartment).nestingLevel}
                                                />
                                            )}
                                        />
                                    </PlainDataProvider>
                                </Field>
                            )}
                        </div>

                        <div className={bem('primary-col-2')}>
                            <Field title={messages.shipToField}>
                                <CustomerDataProvider>
                                    <DropdownEditor
                                        value={shipTo || null}
                                        qa={qa('ship-to-editor')}
                                        onChange={this.props.changeQbooksShippingCustomer}
                                        placeholder={messages.shipToPlaceholder}
                                    />
                                </CustomerDataProvider>
                            </Field>

                            <Field className={bem('primary-row-2')} title={messages.shipViaField}>
                                <TextEditor
                                    value={shipVia || ''}
                                    qa={qa('ship-via-editor')}
                                    onChange={this.props.changeQbooksShippingMethod}
                                    maxLength={constants.qBooksConstants.SHIPPING_METHOD_MAX_LENGTH}
                                    placeholder={messages.shipViaPlaceholder}
                                    changeOnBlur
                                />
                            </Field>
                        </div>

                        <div className={bem('primary-col-3')}>
                            <Field className={bem('field')} title={messages.shippingAddressField}>
                                <TextAreaEditor
                                    minHeight={4}
                                    maxHeight={12}
                                    maxLength={constants.qBooksConstants.SHIPPING_ADDRESS_MAX_LENGTH}
                                    qa={qa('shipping-address-editor')}
                                    value={shippingAddress || ''}
                                    onChange={this.props.changeQbooksShippingAddress}
                                    placeholder={messages.shippingAddressPlaceholder}
                                    changeOnBlur
                                />
                            </Field>
                        </div>
                    </div>

                    {allCustomFields.length > 0 && (
                        <div className={bem('custom-fields')} data-qa={qa('custom-fields')}>
                            {allCustomFields.map((cf) => {
                                const fieldValue = customFields.find((x) => x.field.id === cf.id);

                                return (
                                    <div className={bem('custom-field-item')} key={cf.id}>
                                        <Field key={cf.id} title={cf.text}>
                                            <TextEditor
                                                qa={cf.text}
                                                value={fieldValue && fieldValue.value ? fieldValue.value : ''}
                                                onChange={(newValue) =>
                                                    this.props.changeQbooksCustomField(cf.id, newValue)
                                                }
                                                placeholder={messages.customFieldPlaceholder}
                                                changeOnBlur
                                            />
                                        </Field>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </SectionContainer>
        );
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(QBooksShippingSection);
